import { advance, normalizeGym } from "./merge.js";
import { GymSchema } from "./schemas.js";
import { canonicalJson } from "./utils.js";
export function addCalendarException(gym, exception) {
    return GymSchema.parse({
        ...gym,
        exceptions: [...gym.exceptions.filter((entry) => entry.date !== exception.date), exception],
        updatedAt: exception.updatedAt,
        updatedBy: exception.authorId,
    });
}
/** Il contenuto senza la revisione: due slot identici hanno la stessa impronta. */
function slotContent(slot) {
    return canonicalJson({
        id: slot.id,
        weekday: slot.weekday,
        startTime: slot.startTime,
        durationMinutes: slot.durationMinutes,
        capacity: slot.capacity,
        active: slot.active,
    });
}
/**
 * Il coach modifica una bozza intera e la salva in blocco; il merge invece ha
 * bisogno di sapere *quali* slot sono cambiati, altrimenti riscrivere gli orari
 * dal telefono senza toccarli farebbe vincere quella copia su ogni modifica
 * fatta altrove. Qui la bozza viene confrontata con lo stato di partenza: solo
 * cio' che e' davvero cambiato prende la data di adesso.
 *
 * La testata resta ferma se nome e fuso non sono cambiati: cosi' rinominare la
 * palestra da un dispositivo non viene annullato da chi sta solo aggiungendo un
 * orario dall'altro.
 */
export function commitGymDraft(previous, draft, now, authorId) {
    const before = normalizeGym(previous);
    const beforeSlots = new Map(before.slots.map((slot) => [slot.id, slot]));
    const beforeTombstones = new Map(before.retiredSlots.map((entry) => [entry.id, entry]));
    const beforeExceptions = new Map(before.exceptions.map((entry) => [entry.date, entry]));
    const slots = draft.slots.map((slot) => {
        const existing = beforeSlots.get(slot.id);
        const unchanged = existing && slotContent(existing) === slotContent(slot);
        if (unchanged && existing.updatedAt && existing.updatedBy) {
            return { ...slot, updatedAt: existing.updatedAt, updatedBy: existing.updatedBy };
        }
        // La revisione nuova deve battere sia quella dello slot sia la lapide che
        // potrebbe averlo sepolto: rimettere un orario tolto e' una modifica come
        // le altre, e deve valere piu' della rimozione.
        return {
            ...slot,
            updatedAt: advance(advance(now, existing?.updatedAt), beforeTombstones.get(slot.id)?.updatedAt),
            updatedBy: authorId,
        };
    });
    const kept = new Set(slots.map((slot) => slot.id));
    // Le lapidi non si potano mai, nemmeno quando lo slot e' tornato: e' la loro
    // permanenza a impedire che un dispositivo rimasto indietro lo resusciti.
    const retiredSlots = [
        ...before.retiredSlots,
        ...before.slots
            .filter((slot) => !kept.has(slot.id))
            .map((slot) => ({
            id: slot.id,
            updatedAt: advance(advance(now, slot.updatedAt), beforeTombstones.get(slot.id)?.updatedAt),
            updatedBy: authorId,
        })),
    ];
    const exceptions = draft.exceptions.map((entry) => {
        const existing = beforeExceptions.get(entry.date);
        const unchanged = existing &&
            existing.closed === entry.closed &&
            canonicalJson(existing.removedSlotIds) === canonicalJson(entry.removedSlotIds) &&
            canonicalJson(existing.replacementSlots) === canonicalJson(entry.replacementSlots);
        return unchanged
            ? existing
            : { ...entry, updatedAt: advance(now, existing?.updatedAt), authorId };
    });
    const headerChanged = before.name !== draft.name ||
        before.timeZone !== draft.timeZone ||
        before.slotDurationMinutes !== draft.slotDurationMinutes;
    return normalizeGym(GymSchema.parse({
        ...draft,
        slots,
        retiredSlots,
        exceptions,
        updatedAt: headerChanged ? advance(now, before.updatedAt) : before.updatedAt,
        updatedBy: headerChanged ? authorId : before.updatedBy,
    }));
}
export function detectConfigurationConflicts(base, left, right) {
    const fields = [
        "weekday",
        "startTime",
        "durationMinutes",
        "capacity",
        "active",
    ];
    const baseSlots = new Map(base.slots.map((slot) => [slot.id, slot]));
    const leftSlots = new Map(left.slots.map((slot) => [slot.id, slot]));
    const rightSlots = new Map(right.slots.map((slot) => [slot.id, slot]));
    const ids = new Set([...leftSlots.keys(), ...rightSlots.keys()]);
    const conflicts = [];
    for (const slotId of ids) {
        const original = baseSlots.get(slotId);
        const leftSlot = leftSlots.get(slotId);
        const rightSlot = rightSlots.get(slotId);
        if (!original || !leftSlot || !rightSlot)
            continue;
        for (const field of fields) {
            const leftChanged = leftSlot[field] !== original[field];
            const rightChanged = rightSlot[field] !== original[field];
            if (leftChanged && rightChanged && leftSlot[field] !== rightSlot[field]) {
                conflicts.push({ slotId, field, left: leftSlot[field], right: rightSlot[field] });
            }
        }
    }
    return conflicts;
}
